import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router-dom"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import SendIcon from "@mui/icons-material/Send"
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions"
import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import ImageIcon from "@mui/icons-material/Image"
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera"
import MicIcon from "@mui/icons-material/Mic"
import StopIcon from "@mui/icons-material/Stop"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import GroupAddIcon from "@mui/icons-material/GroupAdd"
import SearchIcon from "@mui/icons-material/Search"
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff"
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive"
import { useChatContext } from "../chat/ChatContext"
import { useAuth } from "../auth/AuthContext"
import { scrollbarSx } from "../../shared/scrollbar"
import { useVoiceCall } from "./call/useVoiceCall"
import CallOverlay, { CallButton, GroupCallButton } from "./call/CallOverlay"
import { MONO, CHAT_COLOR, MIN_W, MAX_W, MIN_H, MAX_H, DEFAULT_W, DEFAULT_H, POLL_MS, META_POLL_MS, EMOJIS, AGENT_COLORS, ALLOWED_FILE_EXTENSIONS } from "./constants"
import { compressImage } from "./utils/chatMedia"
import { readFileAsDataUri, validateFileClientSide } from "./utils/chatFile"
import { getMentionAt } from "./utils/chatText"
import { requestNotificationPermission, showBrowserNotification, playMentionSound } from "./utils/notifications"
import { createChatReminder, parseSlashCommand } from "./utils/reminderCommands"
import { VoicePlayer } from "./components/VoicePlayer"
import { ReplyBar } from "./components/ReplyBar"
import { MentionDropdown } from "./components/MentionDropdown"
import { UserInfoPopover } from "./components/UserInfoPopover"
import { ImageLightbox } from "./components/ImageLightbox"
import { MsgBubble } from "./components/MsgBubble"
import { TabBtn } from "./components/TabBtn"
import { AddUserPanel } from "./components/AddUserPanel"
import { GroupBuilderPanel } from "./components/GroupBuilderPanel"
import { GroupSettingsPanel } from "./components/GroupSettingsPanel"
import { NoLinkScreen } from "./components/NoLinkScreen"
import { ThreadPanel } from "./components/ThreadPanel"
import { SearchPanel } from "./components/SearchPanel"
import { ForwardPicker } from "./components/ForwardPicker"
import { PresenceControls } from "./components/PresenceControls"
import { FileChip } from "./components/FileChip"

export default function LiveChat({ windowHost = null, windowVisible = false }) {
  const { isOpen, setIsOpen, pendingVaultLink, clearVaultLink, selectVaultNode, addUnread, clearUnread, addMention, clearMention } = useChatContext()
  const { user, authFetch } = useAuth()
  const navigate = useNavigate()

  const username = user?.ssh_linked ? user.ssh_username : null
  const call = useVoiceCall(username, authFetch)

  const [width, setWidth] = useState(DEFAULT_W)
  const [height, setHeight] = useState(DEFAULT_H)
  const [activeTab, setActiveTab] = useState("global")
  const [showAddUser, setShowAddUser] = useState(false)
  const [openChats, setOpenChats] = useState([])
  const [users, setUsers] = useState([])
  const [profiles, setProfiles] = useState({})
  const [globalMsgs, setGlobalMsgs] = useState([])
  const [privateMsgs, setPrivateMsgs] = useState({})
  const [input, setInput] = useState("")
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [lastSentId, setLastSentId] = useState(null)
  const [agentTyping, setAgentTyping] = useState({})
  const [otherTyping, setOtherTyping] = useState([])
  const [highlightMsgId, setHighlightMsgId] = useState(null)
  const [replyTo, setReplyTo] = useState(null)
  const [imageAttachment, setImageAttachment] = useState(null)
  const [voiceAttachment, setVoiceAttachment] = useState(null)
  const [fileAttachment, setFileAttachment] = useState(null)
  const [attachmentError, setAttachmentError] = useState("")
  const [isRecording, setIsRecording] = useState(false)
  const [recordSeconds, setRecordSeconds] = useState(0)
  const [mentionQuery, setMentionQuery] = useState(null)
  const [profilePopover, setProfilePopover] = useState({ anchorEl: null, sender: null })
  const [lightboxSrc, setLightboxSrc] = useState(null)
  const [networkError, setNetworkError] = useState(false)
  // Group chats
  const [groups, setGroups] = useState([])
  const [groupMsgs, setGroupMsgs] = useState({})
  const [hiddenGroups, setHiddenGroups] = useState(new Set())
  const [mutedGroups, setMutedGroups] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("chat_muted_groups") || "[]")) } catch { return new Set() }
  })
  const [mutedChannels, setMutedChannels] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("chat_muted_channels") || "[]")) } catch { return new Set() }
  })
  const [customStatus, setCustomStatus] = useState("")
  const [dnd, setDnd] = useState(false)
  const [groupBuilder, setGroupBuilder] = useState(null)   // { name, image, members:[] } while creating
  const [groupSettings, setGroupSettings] = useState(null) // group object being edited, or null
  // Global-chat message metadata: reactions, seen_by, thread reply counts (msg.id -> {...})
  const [messageMeta, setMessageMeta] = useState({})
  const [threadPanel, setThreadPanel] = useState(null) // root message object while a thread is open
  const [forwardTarget, setForwardTarget] = useState(null) // { anchorPosition, msg } while the forward picker is open
  const [hasMoreGlobal, setHasMoreGlobal] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [commandNotice, setCommandNotice] = useState(null)
  const [agentStreams, setAgentStreams] = useState({})

  const boxRef = useRef(null)
  const scrollRef = useRef(null)
  const inputRef = useRef(null)
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)
  const attachmentInputRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const recordTimerRef = useRef(null)
  const typingTimerRef = useRef(null)
  const isResizingW = useRef(false)
  const isResizingH = useRef(false)
  const startX = useRef(0)
  const startY = useRef(0)
  const startW = useRef(DEFAULT_W)
  const startH = useRef(DEFAULT_H)
  const lastPrivateTs = useRef({})
  const activeTabRef = useRef("global")
  const embedded = !!windowHost
  const visible = embedded ? windowVisible : isOpen
  const isOpenRef = useRef(visible)
  const initializedChats = useRef(new Set())
  const closedChatsRef = useRef(new Set())
  const profilePopoverOpenRef = useRef(false)
  const callActiveRef = useRef(false)
  const seenGlobalRef = useRef(new Set())
  const seenGroupMsgRef = useRef(new Set())
  const initializedGroups = useRef(new Set())
  const mutedGroupsRef = useRef(mutedGroups)
  const mutedChannelsRef = useRef(mutedChannels)
  const dndRef = useRef(dnd)
  const hiddenGroupsRef = useRef(hiddenGroups)
  const groupsRef = useRef([])
  const seenSentRef = useRef(new Set())
  const globalMsgsRef = useRef([])
  const isPrependRef = useRef(false)
  const callOverlayRef = useRef(null)

  useEffect(() => {
    if (!pendingVaultLink) return
    setInput((v) => v + (v && !v.endsWith(" ") ? " " : "") + "[[" + pendingVaultLink + "]]")
    clearVaultLink()
  }, [pendingVaultLink, clearVaultLink])

  useEffect(() => {
    const onMove = (e) => {
      if (isResizingW.current) setWidth(Math.min(MAX_W, Math.max(MIN_W, startW.current + (e.clientX - startX.current))))
      if (isResizingH.current) setHeight(Math.min(MAX_H, Math.max(MIN_H, startH.current + (startY.current - e.clientY))))
    }
    const onUp = () => { isResizingW.current = false; isResizingH.current = false }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("pointercancel", onUp)
    return () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("pointercancel", onUp)
    }
  }, [])

  const beginResize = useCallback((event, axis) => {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    if (axis === "width") {
      isResizingW.current = true
      startX.current = event.clientX
      startW.current = width
    } else {
      isResizingH.current = true
      startY.current = event.clientY
      startH.current = height
    }
  }, [width, height])

  // Clear unread for the viewed chat only; other channels keep their badge.
  useEffect(() => {
    if (visible && !document.hidden) { clearUnread(activeTab); clearMention() }
  }, [visible, activeTab, clearUnread, clearMention])
  useEffect(() => { activeTabRef.current = activeTab }, [activeTab])
  useEffect(() => { isOpenRef.current = visible }, [visible])
  useEffect(() => { profilePopoverOpenRef.current = profilePopover.anchorEl !== null }, [profilePopover])
  useEffect(() => { hiddenGroupsRef.current = hiddenGroups }, [hiddenGroups])
  useEffect(() => { groupsRef.current = groups }, [groups])
  useEffect(() => { globalMsgsRef.current = globalMsgs }, [globalMsgs])
  useEffect(() => {
    if (!commandNotice) return
    const timer = setTimeout(() => setCommandNotice(null), 5000)
    return () => clearTimeout(timer)
  }, [commandNotice])
  // Keep the call's conversation loaded even when the regular LiveChat window is closed.
  useEffect(() => {
    callActiveRef.current = call.status !== "idle" || call.groupCall.status !== "idle"
    if (call.status === "ringing" && call.peer) {
      setOpenChats(current => current.includes(call.peer) ? current : [...current, call.peer])
      setActiveTab(call.peer)
    } else if (call.groupCall.status === "ringing" && call.groupCall.group?.group_id) {
      setActiveTab("group:" + call.groupCall.group.group_id)
    }
  }, [call.status, call.peer, call.groupCall.status, call.groupCall.group])
  useEffect(() => {
    mutedGroupsRef.current = mutedGroups
    localStorage.setItem("chat_muted_groups", JSON.stringify([...mutedGroups]))
  }, [mutedGroups])
  useEffect(() => {
    mutedChannelsRef.current = mutedChannels
    localStorage.setItem("chat_muted_channels", JSON.stringify([...mutedChannels]))
  }, [mutedChannels])
  useEffect(() => { dndRef.current = dnd }, [dnd])

  // Request browser notification permission once when the user is identified
  useEffect(() => { if (username) requestNotificationPermission() }, [username])

  // Mark currently-loaded global messages as seen once the Global tab is genuinely being
  // viewed (open, visible, active tab). Reads from refs so it can be called from anywhere
  // (message-list effect, tab switch, regaining focus) without extra effect dependencies.
  const markGlobalSeen = useCallback(() => {
    if (!username || activeTabRef.current !== "global" || document.hidden || !isOpenRef.current) return
    const unseen = globalMsgsRef.current
      .filter(m => m.username !== username && !seenSentRef.current.has(m.id))
      .map(m => m.id)
    if (!unseen.length) return
    unseen.forEach(id => seenSentRef.current.add(id))
    fetch("/api/messenger/seen/", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, message_ids: unseen }),
    }).catch(() => {})
  }, [username])

  useEffect(() => { markGlobalSeen() }, [globalMsgs, activeTab, markGlobalSeen])

  // Tabbing back in with the chat open clears the active chat's badge.
  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden && isOpenRef.current) { clearUnread(activeTabRef.current); clearMention(); markGlobalSeen() }
    }
    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [clearUnread, clearMention, markGlobalSeen])

  useEffect(() => {
    if (!visible || embedded) return
    const onDown = (e) => {
      if (e.target.closest("[data-chat-toggle]")) return
      // MUI popovers are portalled outside boxRef. Treat their paper and backdrop as
      // part of LiveChat so dismissing a picker never dismisses the whole chat too.
      if (e.target.closest("[data-chat-overlay], .MuiPopover-root")) return
      // If the profile popover is open, any outside click should close ONLY the popover
      if (profilePopoverOpenRef.current) {
        setProfilePopover({ anchorEl: null, sender: null })
        return
      }
      if (callActiveRef.current) return  // keep open during a call
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setIsOpen(false)
        setEmojiOpen(false)
        setLightboxSrc(null)
        setProfilePopover({ anchorEl: null, sender: null })
      }
    }
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [visible, embedded, setIsOpen])

  useEffect(() => {
    if (!username) return
    const hb = () => fetch("/api/messenger/heartbeat/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username }) }).catch(() => {})
    hb()
    const t = setInterval(hb, 30000)
    return () => clearInterval(t)
  }, [username])

  // Presence arrives via the SSE stream once connected (see below); this REST call
  // only covers the gap before that connection opens (needs a linked username).
  useEffect(() => {
    if (!visible || username) return
    fetch("/api/messenger/users/").then(r => r.json()).then(d => setUsers(d.users ?? [])).catch(() => {})
  }, [visible, username])

  useEffect(() => {
    if (!visible) return
    fetch("/api/auth/users/").then(r => r.json()).then(d => {
      const map = {}
      for (const p of d.users ?? []) map[p.username] = p
      setProfiles(map)
    }).catch(() => {})
  }, [visible])

  // Seed hidden (removed) conversations from the server so removals persist across reloads/devices
  useEffect(() => {
    if (!username) return
    fetch("/api/messenger/hidden/?username=" + encodeURIComponent(username))
      .then(r => r.json())
      .then(d => {
        (d.hidden ?? []).forEach(key => {
          if (!String(key).startsWith("group:")) closedChatsRef.current.add(key)
        })
        setHiddenGroups(new Set((d.hidden ?? []).filter(k => String(k).startsWith("group:")).map(k => k.slice(6))))
      })
      .catch(() => {})
  }, [username])

  useEffect(() => {
    if (!visible || !username) return
    const load = () =>
      fetch("/api/messenger/conversations/?username=" + encodeURIComponent(username))
        .then(r => r.json())
        .then(d => {
          const convs = d.conversations ?? []
          if (convs.length) setOpenChats(prev => {
            const added = convs.filter(u => !prev.includes(u) && !closedChatsRef.current.has(u))
            return added.length ? [...prev, ...added] : prev
          })
        }).catch(() => {})
    load()
    const t = setInterval(load, POLL_MS)
    return () => clearInterval(t)
  }, [visible, username])

  // Load the latest window of global history via REST (no unread counted for existing
  // messages). Uses the cursor-paginated endpoint, not since=0, which would return the
  // OLDEST messages first once the channel exceeds 200 rows.
  useEffect(() => {
    if (!username) return
    fetch("/api/messenger/global-page/?limit=50")
      .then(r => r.json())
      .then(d => {
        setGlobalMsgs(d.messages ?? [])
        setHasMoreGlobal(Boolean(d.has_more))
        // history only — no unread counted
      })
      .catch(() => {})
  }, [username])

  // Periodic bulk refresh of reactions/seen_by/thread_count for on-screen global messages —
  // these can change after a message was delivered, unlike the message itself (REST history
  // load + SSE only seed them once). Reads globalMsgsRef so the interval isn't torn down and
  // rebuilt on every new message.
  useEffect(() => {
    if (!visible || activeTab !== "global") return
    const load = () => {
      const ids = globalMsgsRef.current.slice(-50).map(m => m.id)
      if (!ids.length) return
      fetch("/api/messenger/global-meta/?message_ids=" + ids.map(encodeURIComponent).join(","))
        .then(r => r.json())
        .then(d => setMessageMeta(prev => ({ ...prev, ...(d.meta ?? {}) })))
        .catch(() => {})
    }
    load()
    const t = setInterval(load, META_POLL_MS)
    return () => clearInterval(t)
  }, [visible, activeTab])

  // Load full history once per group — the SSE stream below only delivers messages
  // created after it connects, mirroring how global history is loaded separately from SSE.
  useEffect(() => {
    if (!username) return
    groups.forEach(g => {
      const gid = g.group_id
      if (hiddenGroups.has(gid) || initializedGroups.current.has(gid)) return
      initializedGroups.current.add(gid)
      fetch("/api/messenger/group-messages/?group_id=" + encodeURIComponent(gid) + "&since=0")
        .then(r => r.json())
        .then(d => {
          const msgs = d.messages ?? []
          if (!msgs.length) return
          msgs.forEach(m => seenGroupMsgRef.current.add(m.id))
          setGroupMsgs(prev => {
            const prevList = prev[gid] ?? []
            const ids = new Set(prevList.map(m => m.id))
            const fresh = msgs.filter(m => !ids.has(m.id))
            return fresh.length ? { ...prev, [gid]: [...prevList, ...fresh] } : prev
          })
          // history only — no unread counted
        })
        .catch(() => { initializedGroups.current.delete(gid) })
    })
  }, [username, groups, hiddenGroups])

  // SSE: connect at current time so ONLY genuinely new events arrive (no history replay).
  // With a linked username this one connection also carries presence, this viewer's
  // group list, and their groups' messages — replacing four of the five poll loops
  // this component used to run (private DMs still poll, see messenger_stream docstring).
  useEffect(() => {
    if (!username) return
    const startTs = Date.now() / 1000
    let es = null
    let stopped = false
    let reconnectTimer = null

    const handleMessage = (e) => {
      try {
        const data = JSON.parse(e.data)
        if (data.type === "agent_stream") {
          if (data.event === "start") {
            setAgentTyping(prev => ({ ...prev, [data.agent]: true }))
            setAgentStreams(prev => ({ ...prev, [data.stream_id]: { ...data, text: "", status: "thinking" } }))
          } else if (data.event === "progress") {
            setAgentStreams(prev => ({ ...prev, [data.stream_id]: { ...(prev[data.stream_id] || data), status: data.message || "thinking" } }))
          } else if (data.event === "chunk") {
            setAgentStreams(prev => ({ ...prev, [data.stream_id]: { ...(prev[data.stream_id] || data), ...data, text: (prev[data.stream_id]?.text || "") + (data.chunk || ""), status: "streaming" } }))
          } else if (data.event === "done") {
            setAgentStreams(prev => { const next = { ...prev }; delete next[data.stream_id]; return next })
            setAgentTyping(prev => { const next = { ...prev }; delete next[data.agent]; return next })
          } else if (data.event === "error") {
            setAgentStreams(prev => ({ ...prev, [data.stream_id]: { ...(prev[data.stream_id] || data), text: `⚠️ ${data.message || "Agent error"}`, status: "error" } }))
            setAgentTyping(prev => { const next = { ...prev }; delete next[data.agent]; return next })
            setTimeout(() => setAgentStreams(prev => { const next = { ...prev }; delete next[data.stream_id]; return next }), 8000)
          }
        } else if (data.type === "message") {
          const msg = data.message
          if (seenGlobalRef.current.has(msg.id)) return
          seenGlobalRef.current.add(msg.id)
          setGlobalMsgs(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg])
          // Notify outside the state updater so it fires exactly once
          if (msg.username !== username) {
            const shouldNotify = !dndRef.current && !mutedChannelsRef.current.has("global") && (!isOpenRef.current || document.hidden || activeTabRef.current !== "global")
            if (shouldNotify) {
              addUnread("global", 1)
              const mentionRe = new RegExp(`@${username}\\b`, "i")
              if (msg.text && mentionRe.test(msg.text)) {
                addMention(); playMentionSound()
              }
              showBrowserNotification(msg.username, msg.text)
            }
          }
        } else if (data.type === "presence") {
          setUsers(data.users ?? [])
          const self = (data.users ?? []).find(item => item.username === username)
          if (self) { setCustomStatus(self.custom_status || ""); setDnd(!!self.dnd) }
        } else if (data.type === "group_list") {
          setGroups(data.groups ?? [])
        } else if (data.type === "group_message") {
          const msg = data.message
          if (seenGroupMsgRef.current.has(msg.id)) return
          seenGroupMsgRef.current.add(msg.id)
          const gid = msg.group_id
          if (hiddenGroupsRef.current.has(gid)) return
          const tab = "group:" + gid
          setGroupMsgs(prev => {
            const prevList = prev[gid] ?? []
            return prevList.find(m => m.id === msg.id) ? prev : { ...prev, [gid]: [...prevList, msg] }
          })
          if (msg.username !== username) {
            const shouldNotify = !dndRef.current && !mutedGroupsRef.current.has(gid) &&
              (!isOpenRef.current || document.hidden || activeTabRef.current !== tab)
            if (shouldNotify) {
              addUnread(tab, 1)
              const mentionRe = new RegExp(`@${username}\\b`, "i")
              if (msg.text && mentionRe.test(msg.text)) { addMention(); playMentionSound() }
              if (document.hidden) {
                const g = groupsRef.current.find(x => x.group_id === gid)
                showBrowserNotification(g?.name ?? gid, msg.text)
              }
            }
          }
        }
      } catch (_) {}
    }

    const scheduleReconnect = () => {
      clearTimeout(reconnectTimer)
      if (!stopped) reconnectTimer = setTimeout(connect, 1000)
    }
    async function connect() {
      try {
        const response = await authFetch("/api/messenger/stream-ticket/", { method: "POST", body: "{}" })
        if (!response.ok) throw new Error(`Stream ticket failed: ${response.status}`)
        const { ticket } = await response.json()
        if (stopped) return
        if (!ticket) throw new Error("Stream ticket missing")
        es = new EventSource(`/api/messenger/stream/?since=${startTs}&ticket=${encodeURIComponent(ticket)}`)
        es.onmessage = handleMessage
        es.onerror = () => {
          es?.close()
          scheduleReconnect()
        }
      } catch { scheduleReconnect() }
    }
    connect()

    return () => { stopped = true; clearTimeout(reconnectTimer); es?.close() }
  }, [username, authFetch]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!username || openChats.length === 0) return
    const poll = () => {
      openChats.forEach(other => {
        const since = lastPrivateTs.current[other] ?? 0
        fetch("/api/messenger/private/?viewer=" + encodeURIComponent(username) + "&with=" + encodeURIComponent(other) + "&since=" + since)
          .then(r => r.json())
          .then(d => {
            const msgs = d.messages ?? []
            if (!msgs.length) return
            const isFirstLoad = !initializedChats.current.has(other)
            setPrivateMsgs(prev => {
              const prevList = prev[other] ?? []
              const ids = new Set(prevList.map(m => m.id))
              const fresh = msgs.filter(m => !ids.has(m.id))
              if (!fresh.length) return prev
              if (!isFirstLoad) {
                const incoming = fresh.filter(m => m.from !== username)
                if (incoming.length && !dndRef.current && !mutedChannelsRef.current.has(other) && (!isOpenRef.current || document.hidden || activeTabRef.current !== other)) {
                  addUnread(other, incoming.length)
                  if (username) {
                    const mentionRe = new RegExp(`@${username}\\b`, "i")
                    const mentioned = incoming.some(m => m.text && mentionRe.test(m.text))
                    if (mentioned) { addMention(); playMentionSound() }
                  }
                  if (document.hidden) {
                    incoming.forEach(m => showBrowserNotification(m.from, m.text))
                  }
                }
              }
              return { ...prev, [other]: [...prevList, ...fresh] }
            })
            if (isFirstLoad) initializedChats.current.add(other)
            lastPrivateTs.current[other] = msgs[msgs.length - 1].timestamp
          }).catch(() => {})
      })
    }
    poll()
    const t = setInterval(poll, POLL_MS)
    return () => clearInterval(t)
  }, [username, openChats]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Prepending older messages (loadOlderGlobal) manages scroll position itself so the
    // viewport doesn't jump to the bottom right after the user scrolled up to load more.
    if (isPrependRef.current) { isPrependRef.current = false; return }
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [globalMsgs, privateMsgs, groupMsgs, activeTab, visible])

  useEffect(() => {
    if (!visible || !username) return
    const poll = () => {
      const ch = activeTabRef.current
      const isGlobal = ch === "global"
      const url = isGlobal
        ? `/api/messenger/typing/?channel=global&viewer=${encodeURIComponent(username)}`
        : `/api/messenger/typing/?channel=private&viewer=${encodeURIComponent(username)}&with=${encodeURIComponent(ch)}`
      fetch(url).then(r => r.json()).then(d => setOtherTyping(d.typing ?? [])).catch(() => {})
    }
    poll()
    const t = setInterval(poll, POLL_MS)
    return () => clearInterval(t)
  }, [visible, username, activeTab])

  const sendTypingStatus = useCallback((status) => {
    if (!username) return
    const ch = activeTabRef.current
    const isGlobal = ch === "global"
    const body = { username, channel: isGlobal ? "global" : "private", status }
    if (!isGlobal) body.private_with = ch
    fetch("/api/messenger/typing/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).catch(() => {})
  }, [username])

  const handleJumpToMessage = useCallback((id) => {
    const el = document.getElementById("msg-" + id)
    if (!el) return
    el.scrollIntoView({ behavior: "smooth", block: "nearest" })
    setHighlightMsgId(id)
    setTimeout(() => setHighlightMsgId(null), 1200)
  }, [])

  const triggerAgentMentions = useCallback((text, history, channel, privateWith, replyPayload, groupId) => {
    const mentioned = [...new Set(Array.from(text.matchAll(/@(jarvis|friday)\b/gi), m => m[1].toLowerCase()))]
    if (!mentioned.length) return
    setAgentTyping(prev => ({ ...prev, ...Object.fromEntries(mentioned.map(a => [a, true])) }))
    const postErr = (agent, errText) => {
      if (channel === "private" && privateWith) {
        fetch("/api/messenger/private/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ from: agent, to: username, text: "⚠️ " + errText }) }).catch(() => {})
      } else if (channel === "group" && groupId) {
        fetch("/api/messenger/group-messages/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ group_id: groupId, username: agent, text: "⚠️ " + errText }) }).catch(() => {})
      } else {
        fetch("/api/messenger/global/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username: agent, text: "⚠️ " + errText }) }).catch(() => {})
      }
    }
    for (const agent of mentioned) {
      const body = { agent, message: text, from_user: username, channel: channel ?? "global" }
      if (replyPayload) body.reply_to = replyPayload
      if (channel === "private" && privateWith) body.private_with = privateWith
      else if (channel === "group" && groupId) body.group_id = groupId
      else body.history = history.slice(-50)
      authFetch("/api/messenger/agent-mention/", { method: "POST", body: JSON.stringify(body) })
        .then(async r => {
          const d = await r.json().catch(() => ({}))
          if (!r.ok || d.error) postErr(agent, d.error || (agent + " konnte nicht antworten."))
        })
        .catch(() => postErr(agent, "Verbindungsfehler."))
        .finally(() => setAgentTyping(prev => { const n = { ...prev }; delete n[agent]; return n }))
    }
  }, [username, authFetch])

  const sendMessage = useCallback(async (channel = activeTab) => {
    const targetTab = typeof channel === "string" ? channel : activeTab
    let text = input.trim()
    if (!text && !imageAttachment && !voiceAttachment && !fileAttachment) return
    if (!username) return
    const slash = !imageAttachment && !voiceAttachment && !fileAttachment ? parseSlashCommand(text) : null
    if (slash?.command === "summarize") {
      text = `@jarvis Bitte fasse den bisherigen Verlauf dieses Kanals prägnant zusammen.${slash.argument ? ` Fokus: ${slash.argument}` : ""}`
    } else if (slash?.command === "todo") {
      if (!slash.argument) { setCommandNotice({ error: true, text: "Verwendung: /todo <text>" }); return }
      setInput("")
      try {
        await createChatReminder(slash.argument, `Live Chat · ${targetTab}`)
        setCommandNotice({ text: "Reminder wurde angelegt." })
      } catch (error) { setCommandNotice({ error: true, text: error.message }) }
      return
    } else if (slash?.command === "vault") {
      if (!slash.argument) { setCommandNotice({ error: true, text: "Verwendung: /vault <pfad>" }); return }
      const messages = targetTab === "global" ? globalMsgs : targetTab.startsWith("group:") ? (groupMsgs[targetTab.slice(6)] || []) : (privateMsgs[targetTab] || [])
      setInput("")
      try {
        const response = await authFetch("/api/messenger/vault-snippet/", { method: "POST", body: JSON.stringify({ path: slash.argument, messages }) })
        const data = await response.json()
        if (!response.ok) throw new Error(data.error || `${response.status}`)
        setCommandNotice({ text: `Chat-Auszug in [[${data.path}]] gespeichert.` })
      } catch (error) { setCommandNotice({ error: true, text: error.message }) }
      return
    } else if (text.startsWith("/")) {
      setCommandNotice({ error: true, text: "Unbekannter Befehl. Verfügbar: /summarize, /todo, /vault" })
      return
    }
    setInput(""); setEmojiOpen(false); setMentionQuery(null)
    clearTimeout(typingTimerRef.current)
    sendTypingStatus("idle")
    const mentions = text
      ? [...new Set(Array.from(text.matchAll(/@(\w+)/g), m => m[1].toLowerCase()))].filter(u => users.some(x => x.username.toLowerCase() === u))
      : []
    const replyPayload = replyTo ? { id: replyTo.id, username: replyTo.username, text: replyTo.text.slice(0, 100) } : null
    setReplyTo(null)
    const imgToSend = imageAttachment
    const voiceToSend = voiceAttachment
    const fileToSend = fileAttachment
    setImageAttachment(null); setVoiceAttachment(null); setFileAttachment(null); setAttachmentError("")

    if (targetTab === "global") {
      const body = { username, text }
      if (imgToSend) body.image = imgToSend
      if (voiceToSend) { body.voice = voiceToSend.b64; body.voice_mime = voiceToSend.mime }
      if (fileToSend) { body.file = fileToSend.data; body.file_name = fileToSend.name }
      if (replyPayload) body.reply_to = replyPayload
      if (mentions.length) body.mentions = mentions
      try {
        const r = await fetch("/api/messenger/global/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
        const d = await r.json()
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
        if (d.message) {
          setGlobalMsgs(prev => prev.find(m => m.id === d.message.id) ? prev : [...prev, d.message])
          if (user?.message_effect && user.message_effect !== "none") { setLastSentId(d.message.id); setTimeout(() => setLastSentId(null), 700) }
          if (text) triggerAgentMentions(text, globalMsgs, "global", null, replyPayload)
        }
      } catch (error) {
        if (fileToSend) { setFileAttachment(fileToSend); setAttachmentError(error.message || "Could not send file") }
        else { setNetworkError(true); setTimeout(() => setNetworkError(false), 5000) }
      }
    } else if (targetTab.startsWith("group:")) {
      const gid = targetTab.slice(6)
      const body = { group_id: gid, username, text }
      if (imgToSend) body.image = imgToSend
      if (voiceToSend) { body.voice = voiceToSend.b64; body.voice_mime = voiceToSend.mime }
      if (fileToSend) { body.file = fileToSend.data; body.file_name = fileToSend.name }
      if (replyPayload) body.reply_to = replyPayload
      if (mentions.length) body.mentions = mentions
      try {
        const r = await fetch("/api/messenger/group-messages/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
        const d = await r.json()
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
        if (d.message) {
          seenGroupMsgRef.current.add(d.message.id)
          setGroupMsgs(prev => { const pl = prev[gid] ?? []; return pl.find(m => m.id === d.message.id) ? prev : { ...prev, [gid]: [...pl, d.message] } })
          if (user?.message_effect && user.message_effect !== "none") { setLastSentId(d.message.id); setTimeout(() => setLastSentId(null), 700) }
          if (text) triggerAgentMentions(text, groupMsgs[gid] ?? [], "group", null, replyPayload, gid)
        }
      } catch (error) {
        if (fileToSend) { setFileAttachment(fileToSend); setAttachmentError(error.message || "Could not send file") }
        else { setNetworkError(true); setTimeout(() => setNetworkError(false), 5000) }
      }
    } else {
      const other = targetTab
      const body = { from: username, to: other, text }
      if (imgToSend) body.image = imgToSend
      if (voiceToSend) { body.voice = voiceToSend.b64; body.voice_mime = voiceToSend.mime }
      if (fileToSend) { body.file = fileToSend.data; body.file_name = fileToSend.name }
      if (replyPayload) body.reply_to = replyPayload
      if (mentions.length) body.mentions = mentions
      try {
        const r = await fetch("/api/messenger/private/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
        const d = await r.json()
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
        if (d.message) {
          setPrivateMsgs(prev => { const pl = prev[other] ?? []; return pl.find(m => m.id === d.message.id) ? prev : { ...prev, [other]: [...pl, d.message] } })
          lastPrivateTs.current[other] = d.message.timestamp
          if (user?.message_effect && user.message_effect !== "none") { setLastSentId(d.message.id); setTimeout(() => setLastSentId(null), 700) }
          if (text) triggerAgentMentions(text, privateMsgs[other] ?? [], "private", other, replyPayload)
        }
      } catch (error) {
        if (fileToSend) { setFileAttachment(fileToSend); setAttachmentError(error.message || "Could not send file") }
        else { setNetworkError(true); setTimeout(() => setNetworkError(false), 5000) }
      }
    }
  }, [input, username, activeTab, user, globalMsgs, privateMsgs, groupMsgs, triggerAgentMentions, imageAttachment, voiceAttachment, fileAttachment, replyTo, users, sendTypingStatus, authFetch])

  const handleRemind = useCallback(async (msg) => {
    try {
      const sourceUrl = new URL(window.location.href)
      sourceUrl.searchParams.set("chat", activeTab)
      sourceUrl.searchParams.set("message", msg.id)
      await createChatReminder(msg.text, `Live Chat · ${activeTab} · ${sourceUrl}`)
      setCommandNotice({ text: "Reminder wurde angelegt." })
    } catch (error) { setCommandNotice({ error: true, text: error.message }) }
  }, [activeTab])

  const savePresence = useCallback(async (status, nextDnd) => {
    const previous = { status: customStatus, dnd }
    setCustomStatus(status)
    setDnd(nextDnd)
    try {
      const response = await authFetch("/api/messenger/presence/", { method: "POST", body: JSON.stringify({ custom_status: status, dnd: nextDnd }) })
      if (!response.ok) throw new Error(`${response.status}`)
    } catch {
      setCustomStatus(previous.status)
      setDnd(previous.dnd)
      setCommandNotice({ error: true, text: "Status konnte nicht gespeichert werden." })
    }
  }, [authFetch, customStatus, dnd])

  const handleVaultLink = useCallback((path) => { selectVaultNode(path); navigate("/vault") }, [selectVaultNode, navigate])

  const addUser = useCallback((u) => {
    closedChatsRef.current.delete(u)
    if (username) {
      fetch("/api/messenger/hidden/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, conv_key: u, action: "unhide" }),
      }).catch(() => {})
    }
    setOpenChats(prev => prev.includes(u) ? prev : [...prev, u]); setActiveTab(u); setShowAddUser(false)
  }, [username])

  const closePrivateChat = useCallback((u) => {
    closedChatsRef.current.add(u)
    if (username) {
      fetch("/api/messenger/hidden/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, conv_key: u }),
      }).catch(() => {})
    }
    setOpenChats(prev => prev.filter(x => x !== u))
    setPrivateMsgs(prev => { const n = { ...prev }; delete n[u]; return n })
    initializedChats.current.delete(u)
    if (activeTab === u) setActiveTab("global")
  }, [activeTab, username])

  const createGroup = useCallback(async (name, members, image) => {
    if (!username || !name.trim()) return
    try {
      const r = await fetch("/api/messenger/groups/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), members, created_by: username, image: image || "" }),
      })
      const d = await r.json()
      if (d.group) {
        setGroups(prev => prev.find(g => g.group_id === d.group.group_id) ? prev : [...prev, d.group])
        setActiveTab("group:" + d.group.group_id)
        setShowAddUser(false); setGroupBuilder(null)
      }
    } catch {
      setNetworkError(true); setTimeout(() => setNetworkError(false), 5000)
    }
  }, [username])

  const saveGroupSettings = useCallback(async (groupId, patch) => {
    try {
      const r = await fetch("/api/messenger/groups/update/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_id: groupId, ...patch }),
      })
      const d = await r.json()
      if (d.group) setGroups(prev => prev.map(g => g.group_id === groupId ? d.group : g))
    } catch {
      setNetworkError(true); setTimeout(() => setNetworkError(false), 5000)
    }
  }, [])

  const closeGroup = useCallback((groupId) => {
    setHiddenGroups(prev => new Set(prev).add(groupId))
    if (username) {
      fetch("/api/messenger/hidden/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, conv_key: "group:" + groupId }),
      }).catch(() => {})
    }
    setGroupMsgs(prev => { const n = { ...prev }; delete n[groupId]; return n })
    initializedGroups.current.delete(groupId)
    if (activeTab === "group:" + groupId) setActiveTab("global")
  }, [activeTab, username])

  const toggleGroupMute = useCallback((groupId) => {
    setMutedGroups(prev => {
      const n = new Set(prev)
      if (n.has(groupId)) n.delete(groupId); else n.add(groupId)
      return n
    })
  }, [])

  const toggleActiveMute = useCallback(() => {
    if (activeTab.startsWith("group:")) {
      toggleGroupMute(activeTab.slice(6))
    } else {
      setMutedChannels(prev => {
        const next = new Set(prev)
        next.has(activeTab) ? next.delete(activeTab) : next.add(activeTab)
        return next
      })
    }
    clearUnread(activeTab)
  }, [activeTab, clearUnread, toggleGroupMute])

  const handleImageSelect = useCallback(async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ""
    const b64 = await compressImage(file)
    if (b64) setImageAttachment(b64)
  }, [])

  const handlePaste = useCallback(async (e) => {
    const items = Array.from(e.clipboardData?.items ?? [])
    const imageItem = items.find(item => item.type.startsWith("image/"))
    if (!imageItem) return
    e.preventDefault()
    const file = imageItem.getAsFile()
    if (!file) return
    const b64 = await compressImage(file)
    if (b64) setImageAttachment(b64)
  }, [])

  const handleFileSelect = useCallback(async (e) => {
    const selected = e.target.files?.[0]
    e.target.value = ""
    if (!selected) return
    const error = validateFileClientSide(selected)
    if (error) {
      setAttachmentError(error)
      setFileAttachment(null)
      return
    }
    try {
      setFileAttachment({ data: await readFileAsDataUri(selected), name: selected.name })
      setAttachmentError("")
    } catch {
      setAttachmentError("Could not read file")
      setFileAttachment(null)
    }
  }, [])

  const startRecording = useCallback(async () => {
    if (isRecording) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream)
      const chunks = []
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data) }
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop())
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" })
        const reader = new FileReader()
        reader.onload = () => setVoiceAttachment({ b64: reader.result, mime: blob.type })
        reader.readAsDataURL(blob)
      }
      mr.start()
      mediaRecorderRef.current = mr
      setIsRecording(true); setRecordSeconds(0)
      sendTypingStatus("recording")
      recordTimerRef.current = setInterval(() => setRecordSeconds(s => s + 1), 1000)
      setTimeout(() => { if (mediaRecorderRef.current?.state === "recording") { mediaRecorderRef.current.stop(); setIsRecording(false); clearInterval(recordTimerRef.current) } }, 60000)
    } catch { /* mic permission denied */ }
  }, [isRecording])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      clearInterval(recordTimerRef.current)
      sendTypingStatus("idle")
    }
  }, [isRecording, sendTypingStatus])

  const handleInputChange = useCallback((e) => {
    const val = e.target.value
    setInput(val)
    const cursor = e.target.selectionStart ?? val.length
    setMentionQuery(getMentionAt(val, cursor))
    if (val) {
      clearTimeout(typingTimerRef.current)
      typingTimerRef.current = setTimeout(() => {
        sendTypingStatus("typing")
        typingTimerRef.current = setTimeout(() => sendTypingStatus("idle"), 3000)
      }, 500)
    } else {
      clearTimeout(typingTimerRef.current)
      sendTypingStatus("idle")
    }
  }, [sendTypingStatus])

  const handleMentionSelect = useCallback((uname) => {
    if (mentionQuery === null) return
    const cursor = inputRef.current?.selectionStart ?? input.length
    const before = input.slice(0, cursor)
    const after = input.slice(cursor)
    const lastAt = before.lastIndexOf("@")
    setInput(before.slice(0, lastAt) + "@" + uname + " " + after)
    setMentionQuery(null)
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [input, mentionQuery])

  const handleReply = useCallback((msg) => {
    const sender = msg.username ?? msg.from ?? "?"
    const text = msg.text || (msg.image ? "[image]" : msg.voice ? "[voice]" : "")
    setReplyTo({ id: msg.id, username: sender, text })
    inputRef.current?.focus()
  }, [])

  // Reactions/threads are Global-tab-only for now — thread_id/seen_by only exist on
  // GlobalMessage, and MessageReaction has no per-channel real-time refresh outside the
  // Global SSE+poll path yet (see live-chat/plan-extensions).
  const toggleReaction = useCallback((msgId, emoji) => {
    if (!username) return
    fetch("/api/messenger/react/", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message_id: msgId, username, emoji }),
    })
      .then(r => r.json())
      .then(d => {
        if (!d.reactions) return
        setMessageMeta(prev => ({ ...prev, [msgId]: { ...(prev[msgId] ?? {}), reactions: d.reactions } }))
      })
      .catch(() => {})
  }, [username])

  const openThread = useCallback(async (msg) => {
    const threadId = msg.thread_id || msg.id
    if (!msg.thread_id) {
      setThreadPanel(msg)
      return
    }
    const loadedRoot = globalMsgsRef.current.find(candidate => candidate.id === threadId)
    if (loadedRoot) {
      setThreadPanel(loadedRoot)
      return
    }
    try {
      const response = await fetch("/api/messenger/thread/?thread_id=" + encodeURIComponent(threadId))
      const data = await response.json()
      if (data.root) setThreadPanel(data.root)
    } catch { /* Keep the current channel visible when the root cannot be loaded. */ }
  }, [])
  const closeThread = useCallback(() => setThreadPanel(null), [])

  const openForward = useCallback((anchorEl, msg) => {
    const rect = anchorEl.getBoundingClientRect()
    setForwardTarget({ anchorPosition: { top: rect.top, left: rect.left + rect.width / 2 }, msg })
  }, [])

  const sendForward = useCallback((targetChannel) => {
    const msg = forwardTarget?.msg
    setForwardTarget(null)
    if (!msg || !username) return
    const sender = msg.username ?? msg.from ?? "?"
    const text = "↪ Forwarded from @" + sender + (msg.text ? ":\n" + msg.text : "")
    const body = { text }
    if (msg.image) body.image = msg.image
    if (msg.voice) { body.voice = msg.voice; body.voice_mime = msg.voice_mime }

    if (targetChannel === "global") {
      fetch("/api/messenger/global/", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, ...body }) }).catch(() => {})
    } else if (targetChannel.startsWith("group:")) {
      fetch("/api/messenger/group-messages/", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_id: targetChannel.slice(6), username, ...body }) }).catch(() => {})
    } else {
      fetch("/api/messenger/private/", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from: username, to: targetChannel, ...body }) }).catch(() => {})
    }
  }, [forwardTarget, username])

  const sendThreadReply = useCallback(async (threadId, text) => {
    if (!username) return null
    try {
      const r = await fetch("/api/messenger/thread/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ thread_id: threadId, username, text }),
      })
      const d = await r.json()
      if (d.message) {
        setMessageMeta(prev => {
          const cur = prev[threadId] ?? {}
          return { ...prev, [threadId]: { ...cur, thread_count: (cur.thread_count ?? 0) + 1 } }
        })
        return d.message
      }
    } catch { /* ThreadPanel keeps the typed text on failure via its own input state */ }
    return null
  }, [username])

  // Loads an older page above the currently-loaded window and keeps the viewport anchored
  // to the same messages the user was looking at (classic "prepend without jumping" scroll
  // math: after the DOM grows, shift scrollTop by exactly how much taller it got).
  const loadOlderGlobal = useCallback(async () => {
    if (!username || loadingOlder || !hasMoreGlobal) return
    const oldest = globalMsgsRef.current[0]?.timestamp
    if (oldest === undefined) return
    setLoadingOlder(true)
    try {
      const r = await fetch("/api/messenger/global-page/?before=" + oldest + "&limit=50")
      const d = await r.json()
      const older = d.messages ?? []
      setHasMoreGlobal(Boolean(d.has_more))
      if (older.length) {
        const el = scrollRef.current
        const prevHeight = el?.scrollHeight ?? 0
        isPrependRef.current = true
        setGlobalMsgs(prev => {
          const ids = new Set(prev.map(m => m.id))
          const fresh = older.filter(m => !ids.has(m.id))
          return fresh.length ? [...fresh, ...prev] : prev
        })
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevHeight
        }))
      }
    } catch { /* leave hasMoreGlobal as-is; user can retry */ }
    setLoadingOlder(false)
  }, [username, loadingOlder, hasMoreGlobal])

  const visibleGroups = groups.filter(g => !hiddenGroups.has(g.group_id))
  const activeGroupId = activeTab.startsWith("group:") ? activeTab.slice(6) : null
  const activeGroup = activeGroupId ? groups.find(g => g.group_id === activeGroupId) : null
  const currentMsgs = activeTab === "global"
    ? globalMsgs
    : activeGroupId
      ? (groupMsgs[activeGroupId] ?? [])
      : (privateMsgs[activeTab] ?? [])
  const callGroupId = call.groupCall.group?.group_id
  const callChannel = call.groupCall.status !== "idle" && callGroupId
    ? "group:" + callGroupId
    : call.status !== "idle" && call.peer
      ? call.peer
      : activeTab
  const callMessages = callChannel.startsWith("group:")
    ? (groupMsgs[callChannel.slice(6)] ?? [])
    : (privateMsgs[callChannel] ?? [])
  const callTitle = callChannel.startsWith("group:")
    ? (call.groupCall.group?.name || "Group chat")
    : (profiles[callChannel]?.display_name ?? callChannel)

  const content = (
    <>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageSelect} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleImageSelect} />
      <input ref={attachmentInputRef} type="file" accept={ALLOWED_FILE_EXTENSIONS.join(",")} style={{ display: "none" }} onChange={handleFileSelect} />
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      <UserInfoPopover
        anchorEl={profilePopover.anchorEl}
        sender={profilePopover.sender}
        profiles={profiles}
        users={users}
        onClose={() => setProfilePopover({ anchorEl: null, sender: null })}
      />
      <ForwardPicker
        anchorPosition={forwardTarget?.anchorPosition}
        openChats={openChats}
        groups={visibleGroups}
        profiles={profiles}
        onPick={sendForward}
        onClose={() => setForwardTarget(null)}
      />
      <CallOverlay ref={callOverlayRef} call={call}
        displayName={profiles[call.peer]?.display_name ?? call.peer}
        avatar={profiles[call.peer]?.avatar_b64}
        chat={{ messages: callMessages, title: callTitle, input, onInputChange: handleInputChange,
          onSend: () => sendMessage(callChannel), me: username, profiles }} />
      {visible && (
      <Box ref={boxRef} sx={{ position: embedded ? "absolute" : "fixed", ...(embedded ? { inset: 0, width: "100%", height: "100%" } : { bottom: 0, left: 64, width, height }),
        backgroundColor: "var(--app-bg-alt)", border: "1px solid rgba(245,158,11,0.16)",
        borderBottom: embedded ? "1px solid rgba(245,158,11,0.16)" : "none",
        borderLeft: embedded ? "1px solid rgba(245,158,11,0.16)" : "none",
        display: "flex", flexDirection: "column",
        zIndex: embedded ? 1 : 1300, boxShadow: embedded ? "none" : "4px -4px 36px rgba(var(--app-shadow-rgb),0.65)", overflow: "hidden" }}>

        {!embedded && <Box onPointerDown={(event) => beginResize(event, "height")}
          sx={{ position: "absolute", top: -6, left: 0, right: 0, height: 12, cursor: "ns-resize", touchAction: "none", zIndex: 2, "&:hover": { backgroundColor: "rgba(245,158,11,0.07)" } }} />}
        {!embedded && <Box onPointerDown={(event) => beginResize(event, "width")}
          sx={{ position: "absolute", top: 0, right: -6, bottom: 0, width: 12, cursor: "col-resize", touchAction: "none", zIndex: 2, "&:hover": { backgroundColor: "rgba(245,158,11,0.07)" } }} />}

        {!username ? <NoLinkScreen /> : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", borderBottom: "1px solid rgba(var(--app-fg-rgb),0.06)",
              flexShrink: 0, overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
              <TabBtn label="Global" active={activeTab === "global" && !showAddUser && !threadPanel && !searchOpen}
                onClick={() => { setActiveTab("global"); setShowAddUser(false); setThreadPanel(null); setSearchOpen(false) }}
                color={CHAT_COLOR} muted={mutedChannels.has("global")} />
              {openChats.map(u => (
                <TabBtn key={u} label={profiles[u]?.display_name ?? u}
                  active={activeTab === u && !showAddUser && !groupBuilder && !groupSettings && !threadPanel && !searchOpen}
                  onClick={() => { setActiveTab(u); setShowAddUser(false); setGroupBuilder(null); setGroupSettings(null); setThreadPanel(null); setSearchOpen(false) }}
                  color="var(--app-success)" onClose={() => closePrivateChat(u)}
                  dot={users.find(x => x.username === u)?.online ?? false}
                  status={users.find(x => x.username === u)?.custom_status || ""} muted={mutedChannels.has(u)} />
              ))}
              {visibleGroups.map(g => (
                <TabBtn key={g.group_id} label={g.name}
                  active={activeTab === "group:" + g.group_id && !showAddUser && !groupBuilder && !groupSettings && !threadPanel && !searchOpen}
                  onClick={() => { setActiveTab("group:" + g.group_id); setShowAddUser(false); setGroupBuilder(null); setGroupSettings(null); setThreadPanel(null); setSearchOpen(false) }}
                  color="var(--feature-vault)" avatar={g.image || ""} muted={mutedGroups.has(g.group_id)}
                  onSettings={() => { setGroupSettings(g); setShowAddUser(false); setGroupBuilder(null) }}
                  onClose={() => closeGroup(g.group_id)} />
              ))}
              <Tooltip title="Start private chat" arrow placement="top">
                <IconButton size="small" onClick={() => { setGroupBuilder(null); setGroupSettings(null); setThreadPanel(null); setSearchOpen(false); setShowAddUser(v => !v) }}
                  sx={{ mx: 0.25, color: showAddUser ? CHAT_COLOR : "rgba(var(--app-fg-rgb),0.28)",
                    "&:hover": { color: CHAT_COLOR }, flexShrink: 0 }}>
                  <AddIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="New group chat" arrow placement="top">
                <IconButton size="small" onClick={() => { setShowAddUser(false); setGroupSettings(null); setThreadPanel(null); setSearchOpen(false); setGroupBuilder(b => b ? null : {}) }}
                  sx={{ mr: 0.5, color: groupBuilder ? CHAT_COLOR : "rgba(var(--app-fg-rgb),0.28)",
                    "&:hover": { color: CHAT_COLOR }, flexShrink: 0 }}>
                  <GroupAddIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Search this chat" arrow placement="top">
                <IconButton size="small"
                  onClick={() => { setShowAddUser(false); setGroupBuilder(null); setGroupSettings(null); setThreadPanel(null); setSearchOpen(v => !v) }}
                  sx={{ mr: 0.5, color: searchOpen ? CHAT_COLOR : "rgba(var(--app-fg-rgb),0.28)",
                    "&:hover": { color: CHAT_COLOR }, flexShrink: 0 }}>
                  <SearchIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={(activeTab.startsWith("group:") ? mutedGroups.has(activeTab.slice(6)) : mutedChannels.has(activeTab)) ? "Unmute channel" : "Mute channel"} arrow placement="top">
                <IconButton size="small" onClick={toggleActiveMute}
                  sx={{ color: (activeTab.startsWith("group:") ? mutedGroups.has(activeTab.slice(6)) : mutedChannels.has(activeTab)) ? "var(--app-error)" : "rgba(var(--app-fg-rgb),0.28)" }}>
                  {(activeTab.startsWith("group:") ? mutedGroups.has(activeTab.slice(6)) : mutedChannels.has(activeTab)) ? <NotificationsOffIcon sx={{ fontSize: 15 }} /> : <NotificationsActiveIcon sx={{ fontSize: 15 }} />}
                </IconButton>
              </Tooltip>
              <PresenceControls status={customStatus} dnd={dnd} onSave={savePresence} />
              <CallButton call={call} target={activeTab}
                show={openChats.includes(activeTab) && !showAddUser && !groupBuilder && !groupSettings && !threadPanel && !searchOpen} />
              <GroupCallButton call={call} group={activeGroup}
                show={!!activeGroupId && !showAddUser && !groupBuilder && !groupSettings && !threadPanel && !searchOpen} />
            </Box>

            {showAddUser
              ? <AddUserPanel users={users} openChats={openChats} onAdd={addUser} me={username} />
              : groupBuilder
              ? <GroupBuilderPanel users={users} me={username} onCreate={createGroup} onCancel={() => setGroupBuilder(null)} />
              : groupSettings
              ? <GroupSettingsPanel group={groupSettings} users={users} me={username}
                  muted={mutedGroups.has(groupSettings.group_id)}
                  onSave={(gid, patch) => { saveGroupSettings(gid, patch); setGroupSettings(null) }}
                  onToggleMute={toggleGroupMute} onClose={() => setGroupSettings(null)} />
              : threadPanel
              ? <ThreadPanel root={threadPanel} me={username} profiles={profiles} users={users}
                  onVaultLink={handleVaultLink} onClose={closeThread} onSend={sendThreadReply} />
              : searchOpen
              ? <SearchPanel
                  channel={activeTab === "global" ? "global" : activeGroupId ? "group" : "private"}
                  viewer={username} other={activeGroupId ? null : activeTab} groupId={activeGroupId}
                  profiles={profiles} onClose={() => setSearchOpen(false)} />
              : (
                <>
                  {networkError && (
                    <Box sx={{
                      px: 1.5, py: 0.8, backgroundColor: "color-mix(in srgb, var(--app-error) 12.5%, transparent)",
                      borderBottom: "1px solid color-mix(in srgb, var(--app-error) 25.1%, transparent)",
                      display: "flex", alignItems: "center", gap: 1,
                    }}>
                      <Typography sx={{ fontSize: "0.65rem", color: "var(--app-error)", fontFamily: MONO }}>
                        ⚠ Verbindung unterbrochen — Nachricht nicht gesendet
                      </Typography>
                      <Box onClick={() => setNetworkError(false)} sx={{ ml: "auto", cursor: "pointer", color: "var(--app-error)", fontSize: "0.7rem" }}>✕</Box>
                    </Box>
                  )}
                  {commandNotice && (
                    <Box sx={{ px: 1.5, py: 0.65, backgroundColor: commandNotice.error ? "color-mix(in srgb, var(--app-error) 9.4%, transparent)" : "color-mix(in srgb, var(--app-success) 9.4%, transparent)", borderBottom: `1px solid ${commandNotice.error ? "color-mix(in srgb, var(--app-error) 25.1%, transparent)" : "color-mix(in srgb, var(--app-success) 25.1%, transparent)"}` }}>
                      <Typography sx={{ fontSize: "0.63rem", color: commandNotice.error ? "var(--app-error)" : "var(--app-success)", fontFamily: MONO }}>{commandNotice.text}</Typography>
                    </Box>
                  )}
                  <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", px: 1.25, py: 1,
                    ...scrollbarSx }}>
                    {activeTab === "global" && hasMoreGlobal && (
                      <Typography onClick={loadOlderGlobal}
                        sx={{ textAlign: "center", fontFamily: MONO, fontSize: "0.63rem",
                          color: loadingOlder ? "rgba(var(--app-fg-rgb),0.25)" : CHAT_COLOR,
                          cursor: loadingOlder ? "default" : "pointer", mb: 1,
                          "&:hover": loadingOlder ? {} : { textDecoration: "underline" } }}>
                        {loadingOlder ? "Loading…" : "Load older messages"}
                      </Typography>
                    )}
                    {currentMsgs.length === 0 && (
                      <Typography sx={{ color: "rgba(var(--app-fg-rgb),0.17)", fontFamily: MONO, fontSize: "0.67rem", textAlign: "center", mt: 3 }}>
                        {activeTab === "global"
                          ? "No messages yet. Say hello."
                          : activeGroupId
                            ? "No messages in " + (activeGroup?.name ?? "this group") + " yet."
                            : "Start a private chat with " + (profiles[activeTab]?.display_name ?? activeTab) + "."}
                      </Typography>
                    )}
                    {currentMsgs.map(msg => {
                      const meta = activeTab === "global" ? messageMeta[msg.id] : null
                      return (
                        <MsgBubble key={msg.id} msg={msg} me={username} profiles={profiles} users={users}
                          onVaultLink={handleVaultLink} showEffect={msg.id === lastSentId}
                          effect={user?.message_effect ?? "none"}
                          onReply={handleReply}
                          onProfileClick={(el, sender) => setProfilePopover({ anchorEl: el, sender })}
                          onImageClick={setLightboxSrc}
                          onJumpToMessage={handleJumpToMessage}
                          highlighted={msg.id === highlightMsgId}
                          reactions={activeTab === "global" ? (meta?.reactions ?? msg.reactions) : undefined}
                          seenBy={activeTab === "global" ? (meta?.seen_by ?? msg.seen_by) : undefined}
                          threadCount={activeTab === "global" ? (meta?.thread_count ?? msg.thread_count ?? 0) : undefined}
                          onReact={activeTab === "global" ? toggleReaction : undefined}
                          onOpenThread={activeTab === "global" ? openThread : undefined}
                          onForward={openForward}
                          onRemind={handleRemind}
                        />
                      )
                    })}
                    {Object.values(agentStreams).filter(stream =>
                      (stream.channel === "global" && activeTab === "global") ||
                      (stream.channel === "group" && activeGroupId === stream.channel_key) ||
                      (stream.channel === "private" && stream.audience?.includes(username) && stream.audience?.includes(activeTab))
                    ).map(stream => (
                      <MsgBubble key={stream.stream_id}
                        msg={{ id: `stream-${stream.stream_id}`, username: stream.agent, text: stream.text || `💭 ${stream.status || "thinking"}…`, timestamp: Date.now() / 1000 }}
                        me={username} profiles={profiles} users={users} onVaultLink={handleVaultLink} />
                    ))}
                  </Box>

                  {emojiOpen && (
                    <Box sx={{ borderTop: "1px solid rgba(var(--app-fg-rgb),0.05)", p: 0.75,
                      display: "flex", flexWrap: "wrap", gap: 0.3,
                      backgroundColor: "var(--app-paper)", flexShrink: 0 }}>
                      {EMOJIS.map(e => (
                        <Box key={e} onClick={() => setInput(v => v + e)}
                          sx={{ fontSize: "1.05rem", cursor: "pointer", p: "2px 3px",
                            borderRadius: "3px", "&:hover": { backgroundColor: "rgba(var(--app-fg-rgb),0.08)" }, userSelect: "none" }}>
                          {e}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {imageAttachment && (
                    <Box sx={{ borderTop: "1px solid rgba(var(--app-fg-rgb),0.05)", px: 1.25, py: 0.5,
                      display: "flex", alignItems: "center", gap: 0.75,
                      backgroundColor: "var(--app-paper)", flexShrink: 0 }}>
                      <Box component="img" src={imageAttachment}
                        sx={{ height: 40, width: 40, objectFit: "cover", borderRadius: 1 }} />
                      <Typography sx={{ flex: 1, fontFamily: MONO, fontSize: "0.62rem", color: "rgba(var(--app-fg-rgb),0.4)" }}>
                        Image attached
                      </Typography>
                      <IconButton size="small" onClick={() => setImageAttachment(null)}
                        sx={{ p: 0, color: "rgba(var(--app-fg-rgb),0.3)", "&:hover": { color: "var(--app-error)" } }}>
                        <CloseIcon sx={{ fontSize: 11 }} />
                      </IconButton>
                    </Box>
                  )}

                  {voiceAttachment && (
                    <Box sx={{ borderTop: "1px solid rgba(var(--app-fg-rgb),0.05)", px: 1.25, py: 0.5,
                      display: "flex", alignItems: "center", gap: 0.75,
                      backgroundColor: "var(--app-paper)", flexShrink: 0 }}>
                      <VoicePlayer voice={voiceAttachment.b64} mime={voiceAttachment.mime} />
                      <IconButton size="small" onClick={() => setVoiceAttachment(null)}
                        sx={{ p: 0, ml: "auto", color: "rgba(var(--app-fg-rgb),0.3)", "&:hover": { color: "var(--app-error)" } }}>
                        <CloseIcon sx={{ fontSize: 11 }} />
                      </IconButton>
                    </Box>
                  )}

                  {(fileAttachment || attachmentError) && (
                    <Box sx={{ borderTop: "1px solid rgba(var(--app-fg-rgb),0.05)", px: 1.25, py: 0.5,
                      display: "flex", alignItems: "center", gap: 0.75,
                      backgroundColor: "var(--app-paper)", flexShrink: 0 }}>
                      {fileAttachment
                        ? <FileChip file={fileAttachment.data} fileName={fileAttachment.name} downloadable={false} />
                        : <Typography sx={{ flex: 1, fontFamily: MONO, fontSize: "0.62rem", color: "var(--app-error)" }}>{attachmentError}</Typography>}
                      <IconButton size="small" onClick={() => { setFileAttachment(null); setAttachmentError("") }}
                        sx={{ p: 0, ml: "auto", color: "rgba(var(--app-fg-rgb),0.3)", "&:hover": { color: "var(--app-error)" } }}>
                        <CloseIcon sx={{ fontSize: 11 }} />
                      </IconButton>
                    </Box>
                  )}

                  {(otherTyping.length > 0 || Object.keys(agentTyping).length > 0) && (
                    <Box sx={{ px: 1.25, py: 0.4, display: "flex", flexWrap: "wrap", gap: 0.75, flexShrink: 0 }}>
                      {otherTyping.map(u => (
                        <Typography key={u.username} sx={{ fontFamily: MONO, fontSize: "0.6rem",
                          color: u.status === "recording" ? "var(--app-success)" : "rgba(var(--app-fg-rgb),0.5)",
                          fontStyle: "italic" }}>
                          {profiles[u.username]?.display_name ?? u.username}
                          {u.status === "recording" ? " is recording 🎤" : " is typing…"}
                        </Typography>
                      ))}
                      {Object.keys(agentTyping).map(agent => (
                        <Typography key={agent} sx={{ fontFamily: MONO, fontSize: "0.6rem",
                          color: AGENT_COLORS[agent], opacity: 0.8, fontStyle: "italic" }}>
                          {agent} is thinking…
                        </Typography>
                      ))}
                    </Box>
                  )}

                  <ReplyBar replyTo={replyTo} onCancel={() => setReplyTo(null)} profiles={profiles} />

                  <Box sx={{ borderTop: "1px solid rgba(var(--app-fg-rgb),0.06)", px: 0.75, py: 0.6,
                    display: "flex", flexDirection: "column", gap: 0.5,
                    flexShrink: 0, backgroundColor: "var(--app-bg)", position: "relative" }}>
                    {mentionQuery !== null && (
                      <MentionDropdown query={mentionQuery.query} users={users} onSelect={handleMentionSelect} />
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <IconButton size="small" onClick={() => setEmojiOpen(v => !v)}
                        sx={{ color: emojiOpen ? "var(--app-warning)" : "rgba(var(--app-fg-rgb),0.28)",
                          "&:hover": { color: "var(--app-warning)" }, flexShrink: 0 }}>
                        <EmojiEmotionsIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <Tooltip title="Attach image (or paste)" arrow placement="top">
                        <IconButton size="small" onClick={() => fileInputRef.current?.click()}
                          sx={{ color: imageAttachment ? CHAT_COLOR : "rgba(var(--app-fg-rgb),0.28)",
                            "&:hover": { color: CHAT_COLOR }, flexShrink: 0 }}>
                          <ImageIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Take photo" arrow placement="top">
                        <IconButton size="small" onClick={() => cameraInputRef.current?.click()}
                          sx={{
                            color: "rgba(var(--app-fg-rgb),0.28)",
                            "&:hover": { color: CHAT_COLOR }, flexShrink: 0,
                            display: { xs: "inline-flex", lg: "none" },
                          }}>
                          <PhotoCameraIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Attach file (PDF, text, archive; max 8 MB)" arrow placement="top">
                        <IconButton size="small" onClick={() => attachmentInputRef.current?.click()}
                          sx={{ color: fileAttachment ? CHAT_COLOR : "rgba(var(--app-fg-rgb),0.28)",
                            "&:hover": { color: CHAT_COLOR }, flexShrink: 0 }}>
                          <AttachFileIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isRecording ? ("Stop (" + recordSeconds + "s)") : "Voice message"} arrow placement="top">
                        <IconButton size="small" onClick={isRecording ? stopRecording : startRecording}
                          sx={{
                            color: isRecording ? "var(--app-error)" : voiceAttachment ? "var(--app-success)" : "rgba(var(--app-fg-rgb),0.28)",
                            "&:hover": { color: isRecording ? "var(--app-error)" : "var(--app-success)" }, flexShrink: 0,
                            "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } },
                            animation: isRecording ? "pulse 1s infinite" : "none",
                          }}>
                          {isRecording ? <StopIcon sx={{ fontSize: 16 }} /> : <MicIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                      <TextField inputRef={inputRef} fullWidth size="small" multiline maxRows={3}
                        value={input} onChange={handleInputChange}
                        onPaste={handlePaste}
                        onKeyDown={(e) => {
                          if (e.key === "Escape" && mentionQuery !== null) { e.preventDefault(); setMentionQuery(null); return }
                          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage() }
                        }}
                        placeholder={activeTab === "global" ? "Message #global…" : activeGroupId ? ("Message " + (activeGroup?.name ?? "group") + "…") : ("Message " + (profiles[activeTab]?.display_name ?? activeTab) + "…")}
                        sx={{
                          "& .MuiInputBase-root": { backgroundColor: "transparent", p: "4px 8px" },
                          "& .MuiInputBase-input": { fontFamily: MONO, fontSize: "0.71rem", color: "rgba(var(--app-fg-rgb),0.88)", p: 0 },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "rgba(var(--app-fg-rgb),0.07)" },
                            "&:hover fieldset": { borderColor: "rgba(245,158,11,0.22)" },
                            "&.Mui-focused fieldset": { borderColor: "rgba(245,158,11,0.42)" },
                          },
                        }}
                      />
                      <IconButton size="small" onClick={sendMessage}
                        disabled={!input.trim() && !imageAttachment && !voiceAttachment && !fileAttachment}
                        sx={{ color: (input.trim() || imageAttachment || voiceAttachment || fileAttachment) ? CHAT_COLOR : "rgba(var(--app-fg-rgb),0.18)",
                          "&:hover": { color: "var(--app-warning)" }, flexShrink: 0 }}>
                        <SendIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </>
              )
            }
          </>
        )}
      </Box>
      )}
    </>
  )
  return windowHost ? createPortal(content, windowHost) : content
}
